import {
  AccordionSummary,
  Box,
  CircularProgress,
  Grid,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import messages from 'components/Refunds/messages';
import { StyledCaption, StyledShortBody } from 'components/Refunds/styles';
import StatusChip from 'components/StatusChip';
import { ShortBody } from 'components/Typography';
import { SINISTER_DOCUMENT_TYPES } from 'constants/globalConstants';
import { StyledAccordionDetails } from 'containers/AmcDetailsPage/MyRefundsTab/Refunds/styles';
import { translate } from 'helpers/intl';
import { get } from 'lodash';
import React, { useEffect } from 'react';
import { IntlShape, injectIntl, useIntl } from 'react-intl';
import labels from 'utils/labels';
import { AssuranceIcon } from 'images';
import { PHYSIQUE_DOCS } from 'utils/amcUtils';
import { DocumentParams, RefundDetails } from '.';
import ClaimButton from './ClaimButton';

interface ComplementaryInfoClaimType {
  intl: IntlShape;
  refund: {
    statusApplication: String;
    maladyDeclarationSuffix: String;
    numeroPolicy: string;
    numeroApplication: Number;
    beneficiary: String;
    dateNum: Number;
    years: Number;
    dateOccu: string;
  };
  refundDetails: RefundDetails;
  isLoading: boolean;
  expanded: boolean | String;
  index: string;
  onDownloadClick: (
    refundDetails: RefundDetails,
    { type, numeroPolicy }: DocumentParams,
  ) => void;
  loading: boolean;
  downloadError: string;
  onCompleteClick: () => void;
  checkExist: () => void;
  isFound: boolean;
  checking: boolean;
}

const ComplementaryInfoClaim = ({
  refund,
  refundDetails,
  expanded,
  index,
  isLoading,
  loading,
  onDownloadClick,
  onCompleteClick,
  downloadError,
  checkExist,
  isFound,
  checking,
  ...props
}: ComplementaryInfoClaimType) => {
  const { formatMessage } = useIntl();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  useEffect(() => {
    async function checkExistComplementary() {
      checkExist();
    }
    if (refundDetails && expanded === index) {
      checkExistComplementary();
    }
  }, [refundDetails]);

  const CompleteRefundButton = () => {
    return (
      <Grid item xs={6} sm>
        <Grid container direction="column">
          {refundDetails?.hasDepotCI &&
            !PHYSIQUE_DOCS.includes(refundDetails.motif as string) && (
              <ClaimButton
                onClick={() => {
                  onCompleteClick();
                }}
                loading={checking}
                title={formatMessage(messages.completeCITitle)}
                buttonTitle={
                  isMobile ? labels.completeCIShort : labels.completeCI
                }
                icon={
                  <img
                    alt="completeCI"
                    src={AssuranceIcon['completeCI']}
                    style={{ height: 18, width: 18 }}
                  />
                }
                disabled={isFound}
                testId="complete-ci"
              />
            )}
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <Box width="100%">
          <Grid container justify="space-between">
            <Grid item xs={12} sm={3}>
              <Grid item>
                <StyledCaption>
                  {translate(get(messages, 'folderNumber'), props)}
                </StyledCaption>
              </Grid>
              <Grid item>
                <Box marginBottom={0}>
                  <StyledShortBody>
                    {refund.numeroApplication +
                      '' +
                      refund.maladyDeclarationSuffix}
                  </StyledShortBody>
                </Box>
              </Grid>
            </Grid>
            <Grid item xs={6} sm>
              <Grid container direction="column">
                <Grid item>
                  <StyledCaption>
                    {translate(get(messages, 'Beneficiary'), props)}
                  </StyledCaption>
                </Grid>
                <Grid item>
                  <ShortBody>{refund.beneficiary}</ShortBody>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6} sm>
              <Grid container direction="column">
                <Grid item>
                  <StyledCaption>
                    {translate(get(messages, 'status'), props)}
                  </StyledCaption>
                </Grid>
                <Grid item>
                  <Box marginBottom={0}>
                    <StatusChip
                      status={'warning'}
                      label={translate(
                        get(messages, 'statusComplementaryInfo'),
                        props,
                      )}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </AccordionSummary>
      {expanded &&
        index === expanded &&
        (isLoading ? (
          <Grid
            container
            justify="center"
            style={{ marginTop: 40, marginBottom: 40 }}
          >
            <CircularProgress color="inherit" size={20} />
          </Grid>
        ) : (
          <StyledAccordionDetails>
            <Box width="100%">
              <Grid container justify="space-between">
                <Grid item xs={6} sm>
                  <Grid container direction="column">
                    <Grid item>
                      <StyledCaption>
                        {translate(get(messages, 'costs'), props)}
                      </StyledCaption>
                    </Grid>
                    <Grid item>
                      <ShortBody>
                        {refundDetails.fraisEngagement + ' DH'}
                      </ShortBody>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={6} sm>
                  <Grid container direction="column">
                    <Grid item>
                      <StyledCaption>
                        {translate(get(messages, 'lossDate'), props)}
                      </StyledCaption>
                    </Grid>
                    <Grid item>
                      <ShortBody>{refund.dateOccu}</ShortBody>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={6} sm>
                  <Grid container direction="column">
                    <Grid item>
                      <StyledCaption>
                        {translate(get(messages, 'motif'), props)}
                      </StyledCaption>
                    </Grid>
                    <Grid item>
                      <ShortBody weight="bold">{refundDetails.motif}</ShortBody>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container justify="space-between">
                  <Grid item xs={6} sm>
                    <Grid container direction="column">
                      <ClaimButton
                        onClick={() => {
                          onDownloadClick(refundDetails, {
                            type: SINISTER_DOCUMENT_TYPES.CI,
                            numeroPolicy: refund.numeroPolicy,
                          });
                        }}
                        loading={loading}
                        buttonTitle={
                          isMobile
                            ? labels.downloadFileShort
                            : labels.downloadFile
                        }
                        title={formatMessage(messages.downloadFileTitle)}
                        icon={
                          <img
                            alt="downloadIcon"
                            src={AssuranceIcon['downloadIcon']}
                          />
                        }
                      />
                    </Grid>
                  </Grid>
                  {!isMobile && (
                    <>
                      <CompleteRefundButton />
                      <Grid item xs={6} sm></Grid>
                    </>
                  )}
                </Grid>
                {isMobile && (
                  <Grid container justify="space-between">
                    <CompleteRefundButton />
                  </Grid>
                )}

                {downloadError && (
                  <ShortBody color="red">{downloadError}</ShortBody>
                )}
                {isFound && (
                  <ShortBody color="green">
                    {formatMessage({ id: 'claim.ci.in.progress' })}
                  </ShortBody>
                )}
              </Grid>
            </Box>
          </StyledAccordionDetails>
        ))}
    </>
  );
};

export default injectIntl(ComplementaryInfoClaim);
